import { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  accordionStyles,
  accordionSummaryStyles,
  iconStyles,
  accordionDetailsStyles,
  tableContainerStyles,
} from "./styles";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined";
import { useTranslation } from "react-i18next";
import { formatPrice } from "../../Helpers/formating";
import { catalogName } from "../../Utils/catalogName";
import { getCurrencySymbol } from "../../Utils/CountriesData/getCountriesData";

const RequestServiceAccordian = (props) => {
  const { service, expandedAccordion, handleAccordionChange, disabled } = props;
  const { name, name_he: nameHe, variants = [], variant_count } = service;

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { t, i18n } = useTranslation("view-job-modal", {
    keyPrefix: "request_service_accordian",
  });
  const { t: tServices } = useTranslation("services");
  const { t: tSku } = useTranslation("sku");
  const { t: tUnits } = useTranslation("units");
  const isHebrew = i18n.language === "he";

  const rotatedIconStyles = {
    ...iconStyles,
    ".Mui-expanded &": {
      transform: isHebrew ? "rotate(-90deg)" : "rotate(90deg)",
    },
  };

  const groupVariants = (variants) => {
    const grouped = {};

    variants.forEach((variant) => {
      const key = variant.name || variant.sku;

      if (!grouped[key]) {
        grouped[key] = {
          sku: key,
          variants: [],
          more_info: null,
        };
      }

      grouped[key].variants.push(variant);
    });

    return Object.values(grouped);
  };

  const groupedVariants = groupVariants(variants);

  const isPercentageVariant = (variant) =>
    variant.unit_symbol === "%" || variant.unit === "Percentage";

  const tableConfig = {
    columns: [
      {
        id: "variant",
        label: t("variant"),
        render: (variant) => {
          const variantName = variant.name || variant.sku;
          // Agent-created variants have no sku.json key — Hebrew falls back to name_he.
          return catalogName(tSku, variantName, {
            ns: "sku",
            nameHe: variant.name_he,
            language: i18n.language,
          });
        },
      },
      {
        id: "unit",
        label: t("unit"),
        render: (variant) => {
          const amount = variant.quantity ?? variant.amount;
          if (amount == null) return "-";
          // Percentage-based variants: the "amount" is actually the deal value
          // the member entered (in ₪), not a quantity of the unit.
          if (isPercentageVariant(variant)) {
            const currency = getCurrencySymbol(variant.currency);
            return `${currency}${amount}`;
          }
          const unit = variant.unit ? tUnits(variant.unit, variant.unit) : "";
          return amount + " " + unit;
        },
      },
      {
        id: "price",
        label: t("price"),
        render: (variant) => {
          const basePrice = variant.price_per_unit ?? variant.price;
          // A not-listed variant can still carry a price once the supplier set
          // one in an order edit (COMY1-292) — only fall back to "Not listed"
          // when no price is known.
          if (variant.is_not_listed && basePrice == null) return t("not_listed");
          const amount = variant.quantity ?? variant.amount;
          // Prefer the server-computed total_price (which already handles
          // percentage variants). Fall back to a local calculation only when
          // a real amount/deal-value exists. If the member skipped the deal
          // value on a percentage variant, show "-" instead of fabricating
          // a price from a fallback quantity.
          let totalPrice = null;
          if (variant.total_price != null) {
            totalPrice = variant.total_price;
          } else if (basePrice != null && amount != null && amount > 0) {
            totalPrice = isPercentageVariant(variant)
              ? (basePrice / 100) * amount
              : basePrice * amount;
          }
          if (totalPrice == null) return "-";
          const priceValue = formatPrice(totalPrice);
          const currency = getCurrencySymbol(variant.currency);
          return priceValue ? `${currency}${priceValue}` : t("not_listed");
        },
      },
    ],
  };

  const tableContainerRef = useRef(null);
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);

  useEffect(() => {
    const checkScroll = () => {
      const container = tableContainerRef.current;
      if (!container) return;
      setHasHorizontalScroll(container.scrollWidth > container.clientWidth);
    };

    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, []);

  return (
    <Accordion
      expanded={expandedAccordion === service.id}
      onChange={handleAccordionChange(service.id)}
      sx={accordionStyles}
      disabled={disabled}  // for phase1
    >
      <AccordionSummary expandIcon={null} sx={accordionSummaryStyles}>
        {isHebrew ? (
          <KeyboardArrowLeftOutlinedIcon sx={rotatedIconStyles} />
        ) : (
          <KeyboardArrowRightIcon sx={rotatedIconStyles} />
        )}

        <Typography sx={{ fontSize: "14px", fontWeight: "400" }}>
          {catalogName(tServices, name, {
            ns: "services",
            nameHe,
            language: i18n.language,
          })}{" "}
          <bdi>({t("variants_count", { count: Number(variant_count) || 0 })})</bdi>
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={accordionDetailsStyles}>
        <Box>
          <TableContainer
            ref={tableContainerRef}
            sx={{
              ...tableContainerStyles(isSmallScreen),
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {tableConfig.columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sx={{
                        ...(column.id === "variant"
                          ? {
                              minWidth: "131px",
                              position: "sticky",
                              left: 0,
                              zIndex: 9,
                              borderLeft: "none !important",
                              boxShadow: hasHorizontalScroll
                                ? "4px 0px 8px -3px rgba(0,0,0,0.12)"
                                : "none",
                            }
                          : {}),
                      }}
                      colSpan={column.colSpan || 1}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedVariants.map((group) =>
                  group.variants.map((variant, index) => (
                    <TableRow key={`${variant.id}-${index}`}>
                      {tableConfig.columns.map((column) => {
                        return (
                          <TableCell
                            key={column.id}
                            rowSpan={group.variants.length}
                            colSpan={column.colSpan || 1}
                            sx={{
                              ...(column.id === "variant"
                                ? {
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 1,
                                    borderLeft: "none !important",
                                    boxShadow: hasHorizontalScroll
                                      ? "4px 0px 8px -3px rgba(0,0,0,0.12)"
                                      : "none",
                                  }
                                : {}),
                            }}
                          >
                            {column.render(variant)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default RequestServiceAccordian;
